/**
 * Strategy types for trader dashboard
 *
 * These types match the Rust backend types in arbi-engine/src/executors/strategy/types.rs
 */

import type { ArbitrageOpportunity } from "./opportunity";

/**
 * Strategy execution types
 */
export interface StrategyExecutionRequest {
  [key: string]: unknown;
}

export type StrategyType =
  | "Cross"
  | "HedgedCross"
  | "StandbyHedgedCross"
  | "StandbyHedged"
  | "AdaptiveHedgedCross"
  | "AdaptiveHedgedCrossPreTransfer"
  | "AdaptiveHedgedCrossPostTransfer"
  | "HedgedCrossPostTransfer";

/**
 * Strategy FSM states
 */
export type StrategyState =
  | "PurchaseCrypto"
  | "HedgeSetup"
  | "Transfer"
  | "NoTransfer"
  | "EvaluatePnL"
  | "BuyOpen"
  | "Standby"
  | "SellCrypto"
  | "SellClose"
  | "HedgeClose"
  | "EmergencyClear"
  | "Abort"
  | "Success"
  | "Failure";

/**
 * Order side
 */
export type OrderSide = "Bid" | "Ask";

/**
 * Order type
 */
export type OrderType = "Market" | "Limit";

/**
 * Order status
 */
export type OrderStatus =
  | "Pending"
  | "PartiallyFilled"
  | "Filled"
  | "Cancelled"
  | "Rejected";

/**
 * Position side for futures
 */
export type PositionSide = "Both" | "Long" | "Short";

/**
 * Fill information (individual trade execution)
 */
export interface Fill {
  uid?: string | null;
  price: number;
  volume: number;
  fee?: number | null;
  fee_currency?: string | null;
}

/**
 * Order information
 */
export interface Order {
  order_id: string;
  id: string;
  symbol: string;
  pair: string;
  exchange: string;
  /** Base currency (e.g., "BTC", "ORCA") */
  base: string;
  /** Quote currency (e.g., "USDT", "KRW") */
  quote: string;
  side: OrderSide;
  order_type: OrderType;
  status: OrderStatus;
  volume: number;
  executed_volume: number;
  price: number;
  executed_price: number;
  /** Total cost in quote currency (after fees) */
  total: number | null;
  /** Fee paid */
  paid_fee: number | null;
  /** Fee currency (e.g., "BNB", "USDT", "KRW") */
  paid_fee_asset: string | null;
  /** Conversion rate: quote currency to USDT (e.g., 1.0 for USDT, 0.000769 for KRW) */
  quote_to_usdt_rate: number | null;
  /** Executed trades/fills */
  fills?: Fill[] | null;
  created_at: number;
  updated_at: number;
}

/**
 * Futures order information
 * Matches Rust FuturesOrder struct in arbi-common/src/models/futures/futures_order.rs
 */
export interface FuturesOrder {
  id: string;
  client_order_id: string | null;
  pair: string;
  base: string;
  quote: string;
  exchange: string;
  side: OrderSide;
  position_side: PositionSide;
  order_type: OrderType;
  orig_type: OrderType | null;
  status: OrderStatus;
  price: number;
  avg_price: number | null;
  stop_price: number | null;
  activate_price: number | null;
  price_rate: number | null;
  volume: number;
  executed_volume: number;
  remaining_volume: number;
  time_in_force: string | null;
  reduce_only: boolean;
  close_position: boolean;
  working_type: string | null;
  price_protect: boolean;
  commission: number | null;
  commission_asset: string | null;
  realized_pnl: number | null;
  /** Executed trades/fills */
  fills?: Fill[] | null;
  created_at: number;
  updated_at: number;
}

/**
 * Futures position
 */
export interface Position {
  symbol: string;
  position_side: PositionSide;
  position_amount: number;
  entry_price: number;
  unrealized_pnl: number;
  leverage: number;
  margin_type: string;
  liquidation_price: number;
  mark_price: number;
  updated_at: number;
}

/**
 * Withdrawal record
 */
export interface Withdrawal {
  id: string;
  asset: string;
  amount: number;
  network: string;
  address: string;
  tx_id: string | null;
  status: string;
  created_at: number;
  completed_at: number | null;
}

/**
 * Deposit record
 */
export interface Deposit {
  id: string;
  asset: string;
  amount: number;
  network: string;
  tx_id: string | null;
  status: string;
  created_at: number;
  completed_at: number | null;
}

/**
 * Aggregated funding fee tracking with synchronization state
 *
 * Instead of storing individual funding fee entries, this struct maintains
 * a running total and tracks whether the data is fully synchronized.
 */
export interface FundingFee {
  /** Symbol for the funding fee (e.g., "BTCUSDT") */
  symbol: string;
  /** Total accumulated funding fee income (can be negative if we paid) */
  total_income: number;
  /** Timestamp when funding fee accumulation started (hedge position opened) */
  started_at: number;
  /** Timestamp of the most recent funding fee event (milliseconds since epoch) */
  last_updated_at: number;
  /** Number of funding fee events accumulated into this total */
  event_count: number;
  /** Whether all historical funding fees have been fetched back to started_at */
  synchronized: boolean;
}

/**
 * Hedge configuration
 */
export interface PriceLevel {
  price: number;
  volume: number;
}

export interface RebalancingConfig {
  price_surge_threshold: number;
  liquidation_proximity_threshold: number;
  entry_funding_rate_threshold?: number | null;
  entry_funding_interval_hours?: number;
  spot_futures_gap_magnitude_ratio?: number;
  low_funding_rate_threshold: number;
  funding_time_window_seconds: number;
}

export interface HedgeConfig {
  leverage: number;
  open?: PriceLevel[] | null;
  close?: PriceLevel[] | null;
  rebalancing?: RebalancingConfig | null;
}

/**
 * Profit tier configuration
 */
export interface ProfitTier {
  profit_percentage: number;
  volume_percentage_to_sell: number;
}

export type BuyOpenMode =
  | { type: "Limit" }
  | { type: "Market"; max_notional_usdt: number };

/**
 * Retry counter snapshot
 */
export interface RetryCountSnapshot {
  hedge_setup: number;
  purchase_crypto: number;
  sell_close: number;
}

/**
 * Strategy metrics
 */
export interface StrategyMetrics {
  total_purchased_volume: number;
  total_sold_volume: number;
  total_deposited_volume: number;
  remaining_target_volume: number;
  total_funding_fees: number;
}

/**
 * Real-time price information for dashboard display
 */
export interface RealTimePriceInfo {
  /** Spot best bid price (sell price for spot holdings - Standby state) */
  spot_best_bid: number | null;
  /** Spot best ask price (buy price for spot entry - BuyOpen state) */
  spot_best_ask: number | null;
  /** Futures best ask price (buy price to close short) */
  futures_best_ask: number | null;
  /** Futures best bid price (sell price to open short - for BuyOpen entry) */
  futures_best_bid: number | null;
  /** Futures mark price (for unrealized PnL) */
  futures_mark_price: number | null;
  /** Current funding rate (as decimal, e.g., 0.0001 = 0.01%) */
  funding_rate: number | null;
  /** Next funding time (Unix timestamp in milliseconds) */
  next_funding_time: number | null;
  /** Previous funding time (Unix timestamp in milliseconds, null if not yet set) */
  previous_funding_time: number | null;
  /** Timestamp when this info was fetched (ms) */
  fetched_at: number;
}

/**
 * Income type for Binance income history
 */
export type IncomeType =
  | "Transfer"
  | "WelcomeBonus"
  | "RealizedPnl"
  | "FundingFee"
  | "Commission"
  | "InsuranceClear"
  | "ReferralKickback"
  | "CommissionRebate"
  | "ApiRebate"
  | "ContestReward"
  | "CrossCollateralTransfer"
  | "OptionsSettlement"
  | "OptionsPremiumFee"
  | "AutoExchange"
  | "CoinSwapDeposit"
  | "CoinSwapWithdraw"
  | "PositionLimitIncreaseFee"
  | "InternalTransfer";

/**
 * Income history record from Binance
 */
export interface IncomeHistory {
  symbol: string;
  income_type: IncomeType;
  income: number;
  asset: string;
  info: string;
  time: number;
  tran_id: number;
  trade_id: string;
}

/**
 * User trade record from Binance
 * Note: Field names use camelCase to match the raw Binance API response stored in snapshot
 * The Rust UserTrade struct uses #[serde(rename_all = "camelCase")] so serializes to camelCase
 */
export interface UserTrade {
  buyer: boolean;
  commission: number;
  commissionAsset: string;
  id: number;
  maker: boolean;
  orderId: number;
  price: number;
  qty: number;
  quoteQty: number;
  realizedPnl: number;
  side: string;
  positionSide: string;
  symbol: string;
  time: number;
}

/**
 * Fetched P&L data from exchange API
 */
export type PnLSourceQuality =
  | "PendingExchangeSync"
  | "FetchedVerified"
  | "BinanceArchiveIncomplete"
  | "Unverified";

export type BinanceArchiveIncompleteReason =
  | "RecentExchangeSync"
  | "HistoricalDataUnavailable"
  | "TradeIdentityConflict"
  | "CommissionConversionUnavailable"
  | "ArchivePersistenceUnavailable"
  | "FundingHistoryUnavailable"
  | "LegacyCoverageUnknown";

export interface FetchedPnL {
  /** Total funding fees received/paid */
  income_sum: number;
  /** Total trading commissions paid */
  commission_sum: number;
  /** Total realized P&L from closed positions */
  realized_pnl_sum: number;
  /** Combined hedge P&L = income_sum - commission_sum + realized_pnl_sum */
  hedge_pnl: number;
  /** Raw income history records from Binance */
  income_history: IncomeHistory[];
  /** Raw trade records from Binance */
  user_trades: UserTrade[];
  /** When this data was fetched */
  fetched_at: number;
  /** Whether Binance fill evidence is authoritative for final P&L. */
  source_quality?: PnLSourceQuality;
  /** Explicit reason fetched evidence is not authoritative. */
  incomplete_reason?: BinanceArchiveIncompleteReason | null;
  covered_order_ids?: number[];
  incomplete_order_ids?: number[];
  expected_executed_quantity?: number;
  covered_executed_quantity?: number;
  coverage_checked_at?: number;
}

/**
 * P&L Snapshot captured during EvaluatePnL state
 */
export interface PnLSnapshot {
  /** Total USDT spent on spot purchases */
  spot_spent_usdt: number;
  /** Total USDT earned from spot sales */
  spot_earned_usdt: number;
  /** Total volume purchased */
  total_purchased_volume: number;
  /** Total volume sold */
  total_sold_volume: number;
  /** Base currency being traded */
  base_currency: string;
  /** Calculated hedge P&L from local orders (for verification) */
  calculated_hedge_pnl: number;
  /** Fetched P&L data from exchange API (source of truth) */
  fetched: FetchedPnL;
  /** Final net P&L in USDT */
  net_pnl_usdt: number;
  /** When P&L was evaluated */
  evaluated_at: number;
}

/**
 * Execution context snapshot
 */
export interface ExecutionContextSnapshot {
  opportunity: ArbitrageOpportunity | null;
  position: Position | null;
  hedge_config: HedgeConfig | null;
  profit_tiers: ProfitTier[] | null;
  original_bep: number | null;
  orders: Record<string, Order>;
  futures_orders: Record<string, FuturesOrder>;
  withdrawals: Record<string, Withdrawal>;
  deposits: Record<string, Deposit>;
  /** Aggregated funding fee tracking (single value per strategy) */
  funding_fee: FundingFee | null;
  retry_counts: RetryCountSnapshot;
  metrics: StrategyMetrics;
  /** P&L snapshot from EvaluatePnL state (only present after strategy completes) */
  pnl_snapshot: PnLSnapshot | null;
  /** BuyOpen order mode: Limit or Market with max_notional_usdt */
  buyopen_mode?: BuyOpenMode | null;
  replay_enabled: boolean;
  replay_delay_seconds: number;
  is_replay: boolean;
  replay_source_strategy_id?: string | null;
}

/**
 * Complete strategy information
 */
export interface StrategyInfo {
  strategy_id: string;
  strategy_type: StrategyType;
  current_state: StrategyState;
  execution_context: ExecutionContextSnapshot;
  /** Real-time price information (spot/futures prices, funding rate) */
  realtime_prices: RealTimePriceInfo;
  started_at: number;
  is_running: boolean;
  /** Whether the strategy monitoring is paused */
  is_monitoring_paused: boolean;
}

export interface StrategyListItemV2 {
  strategy_id: string;
  strategy_type: StrategyType;
  current_state: StrategyState;
  is_monitoring_paused: boolean;
  started_at: number;
  detected_at: number;
  symbol: string;
  spot_exchange_name: string;

  budget_usdt: number;
  entry_diff_ratio: number;
  buy_quote: string;
  buyopen_mode: BuyOpenMode | null;
  profit_tiers: ProfitTier[];
  hedge_config: HedgeConfig | null;
  replay_enabled: boolean;
  replay_delay_seconds: number;
  is_replay: boolean;
  replay_source_strategy_id?: string | null;

  spot_avg_price_quote: number | null;
  spot_avg_price_usdt: number | null;
  spot_current_price_quote: number | null;
  spot_current_price_usdt: number | null;
  spot_quote_currency: string | null;
  spot_is_non_usdt_quote: boolean;
  spot_best_ask: number | null;
  futures_avg_price_usdt: number | null;
  futures_current_price_usdt: number | null;
  futures_best_bid: number | null;
  futures_mark_price: number | null;
  /** Defaults to complete when omitted by an older compatible producer. */
  futures_price_data_complete?: boolean;
  futures_price_data_error?: string | null;
  futures_price_data_order_ids?: string[];
  liquidation_price_usdt: number | null;
  liquidation_margin_percent: number | null;

  funding_rate: number | null;
  next_funding_time: number | null;
  previous_funding_time: number | null;
  total_funding_fees_usdt: number;

  spot_total_volume: number;
  spot_remaining_volume: number;
  spot_asked_volume: number;
  futures_total_volume: number;
  futures_remaining_volume: number;
  futures_closed_volume: number;
  filled_profit_tiers: ProfitTier[];
  remaining_profit_tiers: ProfitTier[];

  spot_profit_usdt: number | null;
  spot_realized_pnl_usdt: number;
  futures_profit_usdt: number | null;
  futures_realized_pnl_usdt: number | null;
  net_profit_usdt: number | null;
  spot_current_value_usdt: number;
  spot_cost_basis_usdt: number;
  roi_percent: number | null;
  spot_fees_usdt: number;
  futures_fees_usdt: number;

  traded_notional_usdt: number;
  spot_net_quote_exposure: number;
  spot_quote_asset: string | null;
  futures_net_notional_usdt: number;
  futures_exchange_name: string | null;
}

/**
 * API response for listing strategies
 */
export interface StrategiesListResponse {
  trader_name: string;
  count: number;
  strategies: StrategyInfo[];
}

export interface StrategiesListV2Response {
  trader_name: string;
  count: number;
  strategies: StrategyListItemV2[];
}

/**
 * API response for single strategy
 */
export interface StrategyDetailResponse {
  trader_name: string;
  strategy: StrategyInfo;
}

/**
 * API response for pause action
 */
export interface PauseStrategyResponse {
  status: "ok" | "error";
  message: string;
}

/**
 * State transition record for completed strategy history
 */
export interface CompletedStateTransition {
  /** State transitioned from */
  from_state: StrategyState;
  /** State transitioned to */
  to_state: StrategyState;
  /** Timestamp of transition (milliseconds) */
  timestamp: number;
}

/**
 * P&L summary for completed strategies
 */
export interface CompletedPnLSummary {
  /** Total realized profit/loss in USDT */
  total_pnl_usdt: number;
  /** Profit/loss percentage */
  pnl_percent: number;
  /** Total spot cost in USDT (investment amount) */
  total_spot_cost_usdt: number;
  /** Total spot revenue in USDT */
  total_spot_revenue_usdt: number;
  /** Total futures P&L in USDT */
  total_futures_pnl_usdt: number;
  /** Total funding fees earned/paid in USDT */
  total_funding_fees_usdt: number;
  /** Total commission paid in USDT */
  total_commission_usdt: number;
  /** Whether Binance fill evidence is authoritative for final P&L. */
  source_quality?: PnLSourceQuality;
  incomplete_reason?: BinanceArchiveIncompleteReason | null;
  covered_order_count?: number;
  incomplete_order_count?: number;
  expected_executed_quantity?: number;
  covered_executed_quantity?: number;
  coverage_checked_at?: number;
}

/**
 * Completed strategy information
 */
export interface CompletedStrategyInfo {
  /** Unique strategy identifier (UUID) */
  strategy_id: string;
  /** Type of strategy */
  strategy_type: StrategyType;
  /** Final state (Success, Failure, Abort) */
  final_state: StrategyState;
  /** Base currency traded (e.g., "BTC", "ETH") */
  base_currency: string | null;
  /** Quote currency used for spot purchase (e.g., "KRW", "USDT") */
  quote_currency: string | null;
  /** Buy exchange name */
  buy_exchange: string | null;
  /** Sell exchange name */
  sell_exchange: string | null;
  /** Timestamp when strategy was started (ms) */
  started_at: number;
  /** Timestamp when strategy completed (ms) */
  completed_at: number;
  /** P&L summary with final profit/loss */
  pnl_summary: CompletedPnLSummary;
  /** History of state transitions */
  state_history: CompletedStateTransition[];
  /** Whether this strategy has been marked as paid (for billing/tracking) */
  paid?: boolean;
  /** Profit tier configuration (if configured) */
  profit_tiers?: ProfitTier[];
}

/**
 * API response for listing completed strategies
 */
export interface CompletedStrategiesListResponse {
  trader_name: string;
  count: number;
  strategies: CompletedStrategyInfo[];
}

/**
 * Compact completed strategy item returned by the v2 list endpoint.
 * Full state history remains available through the strategy detail endpoint.
 */
export type CompletedStrategyListItemV2 = Omit<
  CompletedStrategyInfo,
  "state_history"
>;

/**
 * API response for the completed-strategies v2 full-history window.
 */
export interface CompletedStrategiesListV2Response {
  trader_name: string;
  count: number;
  total_count: number;
  completed_from_ms: number;
  completed_to_ms: number;
  unavailable_count: number;
  unavailable_strategy_ids: string[];
  strategies: CompletedStrategyListItemV2[];
}

/**
 * Helper function to get state color
 */
export function getStateColor(state: StrategyState): string {
  switch (state) {
    case "PurchaseCrypto":
    case "HedgeSetup":
      return "#00bcd4"; // cyan - active
    case "Transfer":
    case "NoTransfer":
      return "#ff9800"; // orange - in progress
    case "BuyOpen":
      return "#ff9800"; // orange - monitoring for entry
    case "EvaluatePnL":
    case "Standby":
      return "#4caf50"; // green - waiting
    case "SellCrypto":
    case "SellClose":
    case "HedgeClose":
      return "#2196f3"; // blue - closing
    case "EmergencyClear":
      return "#f44336"; // red - forced liquidation
    case "Success":
      return "#00ff00"; // bright green - complete
    case "Failure":
    case "Abort":
      return "#f44336"; // red - failed
    default:
      return "#9e9e9e"; // grey - unknown
  }
}

/**
 * Helper function to get human-readable state name
 */
export function getStateDisplayName(state: StrategyState): string {
  switch (state) {
    case "PurchaseCrypto":
      return "Purchasing";
    case "HedgeSetup":
      return "Setting Up Hedge";
    case "Transfer":
      return "Transferring";
    case "NoTransfer":
      return "No Transfer";
    case "EvaluatePnL":
      return "Evaluating P&L";
    case "BuyOpen":
      return "Waiting for Entry";
    case "Standby":
      return "Standby";
    case "SellCrypto":
      return "Selling Crypto";
    case "SellClose":
      return "Closing Sell";
    case "HedgeClose":
      return "Closing Hedge";
    case "EmergencyClear":
      return "Emergency Clear";
    case "Abort":
      return "Aborted";
    case "Success":
      return "Completed";
    case "Failure":
      return "Failed";
    default:
      return state;
  }
}

/**
 * Helper function to get Korean localized state name
 */
export function getStateDisplayNameKo(state: StrategyState): string {
  switch (state) {
    case "PurchaseCrypto":
      return "매수 중";
    case "HedgeSetup":
      return "헷지 설정 중";
    case "Transfer":
      return "전송 중";
    case "NoTransfer":
      return "무전송";
    case "EvaluatePnL":
      return "손익 평가";
    case "BuyOpen":
      return "진입";
    case "Standby":
      return "대기";
    case "SellCrypto":
      return "매도 중";
    case "SellClose":
      return "청산 중";
    case "HedgeClose":
      return "헤지 청산";
    case "EmergencyClear":
      return "긴급 청산";
    case "Abort":
      return "중단됨";
    case "Success":
      return "완료";
    case "Failure":
      return "실패";
    default:
      return state;
  }
}

/**
 * Helper function to get strategy type color
 */
export function getStrategyTypeColor(type: StrategyType): string {
  switch (type) {
    case "Cross":
      return "#00bcd4";
    case "HedgedCross":
      return "#9c27b0";
    case "StandbyHedgedCross":
      return "#ff9800";
    case "StandbyHedged":
      return "#4caf50";
    case "AdaptiveHedgedCross":
      return "#e91e63";
    default:
      return "#9e9e9e";
  }
}
